import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { type Observable, switchMap } from 'rxjs';
import type { KeywordInterface } from '../../models/keyword.interface';
import { ModifyKeywordsComponent } from '../card/components/modify-keywords/modify-keywords.component';
import {
  type DialogParams,
  ModalDialogService,
} from '../modal-dialog/modal-dialog.service';
import { ConfirmPopupComponent } from './confirm-popup/confirm-popup.component';
import { PdfViewerPopupComponent } from './pdf-viewer-popup/pdf-viewer-popup.component';
import { ShareEntityPopupInput, ShareEntityPopupResult } from './share-study/components/share-by-app/share-by-app.component';
import { ShareStudyByEmailPopupResult } from './share-study/components/share-by-email/share-by-email.component';
import { ShareByReferingVetPopupResult } from './share-study/components/share-by-refering-vet/share-by-refering-vet.component';

import { ShareStudyComponent } from './share-study/share-study.component';

export type ShareResult = ShareEntityPopupResult | ShareStudyByEmailPopupResult | ShareByReferingVetPopupResult;
import { SccPopupComponent } from './scc-popup/scc-popup.component';
import { InstanceInterface } from '../../models/instance.interface';
import { SccDog, SccService } from '../../../scc/shared/services/scc.service';
import { AppStateService } from '../../services/app-state.service';
import { SccMainDicomTags } from '../../models/scc-main-dicom-tags';
import { SccConfirmDataComponent } from './scc-confirm-data/scc-confirm-data.component';
import { SccDicomDataComponent } from './scc-dicom-data/scc-dicom-data.component';
import { SccStudyDataInterface } from 'src/app/scc/shared/services/scc-study-data.interface';
import { SccRecycleStudyPopupComponent } from './scc-recycle-study-popup/scc-recycle-study-popup.component';

import { GlobalNotificationInput, GlobalNotificationPopupComponent, GlobalNotificationResult } from './global-notification-popup/global-notification-popup.component';
import { GlobalNotificationConfirmInput, GlobalNotificationConfirmPopupComponent } from './global-notification-confirm-popup/global-notification-confirm-popup.component';
import { ClinicNotificationInput, ClinicNotificationPopupComponent, ClinicNotificationResult } from './clinic-notification-popup/clinic-notification-popup.component';
import { SccUploadConfirmedComponent } from './scc-upload-confirmed/scc-upload-confirmed.component';
import { SccPaymentProcessInfoComponent, SCC_PAYMENT_PROCESS_INFO_DIALOG_PARAMS } from '../../../scc/components/scc-payment-method-page/scc-payment-process-info/scc-payment-process-info.component';
import { UserDetailsInput, UserDetailsPopupComponent, UserDetailsResult } from './user-details-popup/user-details-popup.component';
import { CguInput, CguPopupComponent, CguResult } from './cgu-popup/cgu-popup.component';
import { SccSelectDiseasesPopupComponent, SccDiseasesSelectionResult, SccDiseaseSelection } from './scc-select-diseases-popup/scc-select-diseases-popup.component';
import { MatDialog } from '@angular/material/dialog';
import { ImportantAlertPopupComponent, ImportantAlertPopupInput, ImportantAlertPopupResult } from './important-alert-popup/important-alert-popup.component';
import { CreateImportantAlertPopupComponent, CreateImportantAlertInput, CreateImportantAlertResult } from './create-important-alert-popup/create-important-alert-popup.component';



@Injectable()
export class CommonDialogService {
  constructor(
    private readonly keywordsDialogService: ModalDialogService<number[], KeywordInterface[]>,
    private readonly confirmDialogService: ModalDialogService<boolean, undefined>,
    private readonly pdfViewerDialogService: ModalDialogService<void, Document[]>,
    private readonly shareByAppOrEmailDialogService: ModalDialogService<ShareEntityPopupResult | ShareStudyByEmailPopupResult, ShareEntityPopupInput>,
    private readonly shareToSccService: ModalDialogService<InstanceInterface[], { studyId: number }>,
    private readonly confirmDicomDataDialogService: ModalDialogService<boolean, { dicomData: SccMainDicomTags[]; lofData: SccDog }>,
    private readonly dicomDataDialogService: ModalDialogService<void, { dicomData: SccMainDicomTags }>,
    private readonly recycleStudyDialogService: ModalDialogService<number[], SccStudyDataInterface>,
    private readonly globalNotificationDialogService: ModalDialogService<GlobalNotificationResult, GlobalNotificationInput>,
    private readonly clinicNotificationDialogService: ModalDialogService<ClinicNotificationResult, ClinicNotificationInput>,
    private readonly globalNotificationConfirmDialogService: ModalDialogService<boolean, GlobalNotificationConfirmInput>,
    private readonly sccUploadConfirmedDialogService: ModalDialogService<boolean, undefined>,
    private readonly sccPaymentProcessInfoDialogService: ModalDialogService<boolean, undefined>,
    private readonly userDetailsDialogService: ModalDialogService<UserDetailsResult, UserDetailsInput>,
    private readonly sccSelectDiseasesDialogService: ModalDialogService<SccDiseasesSelectionResult | undefined, SccDiseaseSelection[]>,
    private readonly importantAlertDialogService: ModalDialogService<ImportantAlertPopupResult, ImportantAlertPopupInput>,
    private readonly createImportantAlertDialogService: ModalDialogService<CreateImportantAlertResult, CreateImportantAlertInput>,
    private readonly cguDialogService: ModalDialogService<CguResult, CguInput>,
    private readonly translateService: TranslateService,
    private readonly sccService: SccService,
    private readonly appState: AppStateService,
  ) { }

  openShareStudyByAppOrEmail(patientName: string, studyId: number): Observable<ShareResult> {
    const params: DialogParams<ShareEntityPopupInput> = {
      input: { filterClinic: true, entityId : studyId, isStudy: true },
      config: {
        title: this.translateService.instant('popup.share_study.title', { patientName }),
        showSaveButton: true,
        showCancelButton: true,
        minHeight: '30vh',
        minWidth: '30vw',
        cancelButton: {
          label: this.translateService.instant('popup.share_study.cancel'),
          icon: ''
        },
        saveButton: {
          label: this.translateService.instant('popup.share_study.ok'),
          icon: ''
        }
      }
    };
    return this.shareByAppOrEmailDialogService.openDialog(params, ShareStudyComponent as any) as Observable<ShareResult>;
  }

  openShareAlbum(albumName: string, albumId: number): Observable<ShareResult> {
    const params: DialogParams<ShareEntityPopupInput> = {
      input:  { filterClinic: false, entityId : albumId, isStudy : false },
      config: {
        title: this.translateService.instant('popup.share_album.title', { albumName }),
        showSaveButton: true,
        showCancelButton: true,
        cancelButton: {
          label: this.translateService.instant('popup.share_album.cancel'),
          icon: ''
        },
        saveButton: {
          label: this.translateService.instant('popup.share_album.ok'),
          icon: ''
        }
      }
    };
    return this.shareByAppOrEmailDialogService.openDialog(params, ShareStudyComponent as any) as Observable<ShareResult>;
  }

  openAddOrUpdateKeywords(keywords: KeywordInterface[], isFilter = false): Observable<number[]> {
    const params: DialogParams<KeywordInterface[]> = {
      input: keywords,
      config: {
        title: this.translateService.instant(isFilter ? 'popup.filter_keywords.title' : 'popup.add_update_keywords.title' ),
        showSaveButton: true,
        showCancelButton: true,
        cancelButton: {
          label: this.translateService.instant(isFilter ? 'popup.filter_keywords.cancel' : 'popup.add_update_keywords.cancel' ),
          icon: ''
        },
        saveButton: {
          label: this.translateService.instant(isFilter ? 'popup.filter_keywords.save' : 'popup.add_update_keywords.save'),
          icon: ''
        }
      }
    };

    return this.keywordsDialogService.openDialog(params, ModifyKeywordsComponent);
  }

  openBasicConfirmationDialog(title: string, description: string, cancelButtonTitle?: string, confirmButtonTitle?: string, isDanger = true): Observable<boolean> {
    const params: DialogParams<undefined> = {
      config: {
        title,
        description,
        showSaveButton: true,
        showCancelButton: true,
        cancelButton: {
          label: cancelButtonTitle ?? this.translateService.instant('popup.confirm.cancel')
        },
        saveButton: {
          label: confirmButtonTitle ?? this.translateService.instant('popup.confirm.confirm'),
          isDanger
        }
      }
    };

    return this.confirmDialogService.openDialog(params, ConfirmPopupComponent);
  }

  openPdfViewerDialog(urls: Document[]): Observable<void> {
    const params: DialogParams<Document[]> = {
      input: urls,
      config: {
        title: this.translateService.instant('popup.pdf_viewer.title'),
        showSaveButton: true,
        showCancelButton: false,
        minHeight: '400px',
        maxHeight: '600px',
        height: '100%',
        saveButton: {
          label: this.translateService.instant('popup.pdf_viewer.save'),
        }
      }
    };

    return this.pdfViewerDialogService.openDialog(params, PdfViewerPopupComponent);
  }

  openShareToSccDialog(studyId: number): void {
    const params: DialogParams<{ studyId: number }> = {
      input: { studyId },
      config: {
        title: this.translateService.instant('popup.scc.title'),
        showSaveButton: true,
        showCancelButton: true,
        saveButton: {
          label: this.translateService.instant('popup.scc.save')
        }
      }
    };

    this.shareToSccService.openDialog(params, SccPopupComponent)
      .subscribe((instances: InstanceInterface[]) => {
        if (instances.length) {
          this.sccService.generateUploadUrlFromMypacs(this.appState.clinicId ?? 0, instances.map(i => i.publicid)).subscribe(link => {
            window.open(link, 'scc');
          });
        }
      });
  }

  openConfirmDicomDataDialog(dicomData: SccMainDicomTags[], lofData: SccDog): Observable<boolean> {
    const params: DialogParams<{ dicomData: SccMainDicomTags[]; lofData: SccDog }> = {
      input: { dicomData, lofData },
      config: {
        title: this.translateService.instant('popup.scc.confirm_data.title'),
        showSaveButton: true,
        showCancelButton: true,
        saveButton: {
          label: this.translateService.instant('popup.confirm.confirm')
        }
      }
    };

    return this.confirmDicomDataDialogService.openDialog(params, SccConfirmDataComponent);
  }

  openDicomDataDialog(dicomData: SccMainDicomTags): Observable<void> {
    const params: DialogParams<{ dicomData: SccMainDicomTags }> = {
      input: { dicomData },
      config: {
        title: this.translateService.instant('viewer.serie.scc.dicom_data'),
        showSaveButton: false,
        showCancelButton: true,
        cancelButton: {
          label: this.translateService.instant('popup.scc.data.close')
        }
      }
    };

    return this.dicomDataDialogService.openDialog(params, SccDicomDataComponent, true);
  }

  openRecycleStudyDialog(study: SccStudyDataInterface): Observable<number[]> {
    const params: DialogParams<SccStudyDataInterface> = {
      input: study,
      config: {
        title: this.translateService.instant('popup.recycle_scc_study.title'),
        showSaveButton: true,
        showCancelButton: true,
        saveButton: {
          label: this.translateService.instant('popup.confirm.confirm')
        }
      }
    };

    return this.recycleStudyDialogService.openDialog(params, SccRecycleStudyPopupComponent);

  }

  openGlobalNotificationDialog(): Observable<GlobalNotificationResult> {
    const params: DialogParams<GlobalNotificationInput> = {
      config: {
        title: this.translateService.instant('popup.global_notification.title'),
        description: this.translateService.instant('popup.global_notification.description'),
        width: '500px',
        showSaveButton: true,
        showCancelButton: true,
        saveButton: {
          label: this.translateService.instant('popup.global_notification.send'),
          icon: 'send'
        },
        cancelButton: {
          label: this.translateService.instant('popup.global_notification.cancel')
        }
      }
    };

    return this.globalNotificationDialogService.openDialog(params, GlobalNotificationPopupComponent);
  }

  openClinicNotificationDialog(): Observable<ClinicNotificationResult> {
    const params: DialogParams<ClinicNotificationInput> = {
      config: {
        title: this.translateService.instant('popup.clinic_notification.title'),
        description: this.translateService.instant('popup.clinic_notification.description'),
        width: '500px',
        showSaveButton: true,
        showCancelButton: true,
        saveButton: {
          label: this.translateService.instant('popup.global_notification.send'),
          icon: 'send'
        },
        cancelButton: {
          label: this.translateService.instant('popup.global_notification.cancel')
        }
      }
    };

    return this.clinicNotificationDialogService.openDialog(params, ClinicNotificationPopupComponent);
  }

  openGlobalNotificationConfirmDialog(message: string): Observable<boolean> {
    const params: DialogParams<GlobalNotificationConfirmInput> = {
      input: { message },
      config: {
        title: this.translateService.instant('popup.global_notification_confirm.title'),
        description: this.translateService.instant('popup.global_notification_confirm.description'),
        width: '500px',
        showSaveButton: true,
        showCancelButton: true,
        saveButton: {
          label: this.translateService.instant('popup.global_notification_confirm.send'),
          icon: 'send'
        },
        cancelButton: {
          label: this.translateService.instant('popup.global_notification_confirm.cancel')
        }
      }
    };

    return this.globalNotificationConfirmDialogService.openDialog(params, GlobalNotificationConfirmPopupComponent);
  }

  openSccUploadConfirmedDialog(): Observable<boolean> {
    const params: DialogParams<undefined> = {
      config: {
        title: 'Dépôt de dossier confirmé',
        width: '700px',
        height: 'auto',
        maxWidth: '90vw',
        maxHeight: 'none',
        showSaveButton: true,
        showCancelButton: false,
        saveButton: {
          label: 'OK'
        }
      }
    };

    return this.sccUploadConfirmedDialogService.openDialog(params, SccUploadConfirmedComponent);
  }

  openSccPaymentProcessInfoDialog(): Observable<boolean> {
    return this.sccPaymentProcessInfoDialogService.openDialog(SCC_PAYMENT_PROCESS_INFO_DIALOG_PARAMS, SccPaymentProcessInfoComponent);
  }

  openUserDetailsDialog(): Observable<UserDetailsResult> {
    const params: DialogParams<UserDetailsInput> = {
      config: {
        title: this.translateService.instant('popup.user_details.title'),
        showSaveButton: true,
        showCancelButton: false,
        saveButton: {
          label: this.translateService.instant('popup.user_details.save'),
          icon: 'save'
        }
      }
    };

    return this.userDetailsDialogService.openDialog(params, UserDetailsPopupComponent);
  }

  openCguDialog(): Observable<CguResult> {
    return this.translateService.get(['popup.cgu.title', 'popup.cgu.accept']).pipe(
      switchMap(translations => {
        const params: DialogParams<CguInput> = {
          config: {
            title: translations['popup.cgu.title'],
            disableClose: true,
            showSaveButton: true,
            showCancelButton: false,
            saveButton: {
              label: translations['popup.cgu.accept'],
              icon: 'check'
            }
          }
        };
        return this.cguDialogService.openDialog(params, CguPopupComponent);
      })
    );
  }

  openSccSelectDiseasesDialog(availableDiseases: SccDiseaseSelection[]): Observable<SccDiseasesSelectionResult | undefined> {
    const params: DialogParams<SccDiseaseSelection[]> = {
      input: availableDiseases,
      config: {
        title: 'Sélection des dépistages pour l\'appel',
        width: '600px',
        showSaveButton: true,
        showCancelButton: true,
        saveButton: {
          label: 'Continuer vers la sélection du lecteur',
          icon: 'gavel'
        },
        cancelButton: {
          label: 'Annuler'
        }
      }
    };
    return this.sccSelectDiseasesDialogService.openDialog(params, SccSelectDiseasesPopupComponent);
  }

  openImportantAlertDialog(alert: any): Observable<ImportantAlertPopupResult> {
    const params: DialogParams<ImportantAlertPopupInput> = {
      input: { alert },
      config: {
        title: this.translateService.instant('popup.important_alert.title'),
        width: '600px',
        disableClose: true,
        showSaveButton: true,
        showCancelButton: false,
        saveButton: {
          label: this.translateService.instant('popup.important_alert.understood'),
          icon: 'check'
        }
      }
    };
    return this.importantAlertDialogService.openDialog(params, ImportantAlertPopupComponent);
  }

  openCreateImportantAlertDialog(): Observable<CreateImportantAlertResult> {
    const params: DialogParams<CreateImportantAlertInput> = {
      config: {
        title: this.translateService.instant('popup.create_important_alert.title'),
        description: this.translateService.instant('popup.create_important_alert.description'),
        width: '600px',
        showSaveButton: true,
        showCancelButton: true,
        saveButton: {
          label: this.translateService.instant('popup.create_important_alert.create'),
          icon: 'add'
        },
        cancelButton: {
          label: this.translateService.instant('popup.create_important_alert.cancel')
        }
      }
    };

    return this.createImportantAlertDialogService.openDialog(params, CreateImportantAlertPopupComponent);
  }

}
export interface Document {
  url: string;
  label: string;
}
